import React, { useCallback, useState } from 'react';
import { AlertTriangle, CheckCircle2, History, Sparkles, TrendingUp } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { Localized } from '../../types/notice';
import type { AttentionTarget } from '../../utils/requestDashboard';
import type { BriefingDraft, TerritorialBriefing } from '../../utils/territorialBriefing';
import { DATA_AS_OF } from '../../data/requestDashboard';
import { BriefingCard } from './BriefingCard';
import { BriefingDraftDialog } from './BriefingDraftDialog';
interface TerritorialBriefingPanelProps {
  briefing: TerritorialBriefing;
  scopeLabel: Localized | null;
  onOpen: (target: AttentionTarget) => void;
}

/** "Votre briefing territorial": 3 issues + 1 result to share, prepared by ATNA. */
export function TerritorialBriefingPanel({
  briefing,
  scopeLabel,
  onOpen
}: TerritorialBriefingPanelProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [draft, setDraft] = useState<{
    itemId: string;
    draft: BriefingDraft;
  } | null>(null);
  const [sent, setSent] = useState<string[]>([]);
  const closeDraft = useCallback(() => setDraft(null), []);
  const {
    since
  } = briefing;
  const send = () => {
    if (!draft) return;
    setSent(s => [...s, `${draft.itemId}:${draft.draft.kind}`]);
    setDraft(null);
  };
  const sinceCols = [{
    key: 'new',
    label: t('Nouveaux enjeux', 'New issues'),
    icon: TrendingUp,
    color: 'text-amber-600',
    data: since.newIssues
  }, {
    key: 'worse',
    label: t('Situations aggravées', 'Worsened'),
    icon: AlertTriangle,
    color: 'text-red-600',
    data: since.worsened
  }, {
    key: 'resolved',
    label: t('Problèmes résolus', 'Resolved'),
    icon: CheckCircle2,
    color: 'text-emerald-600',
    data: since.resolved
  }];
  return <section className="border border-slate-200 rounded-xl shadow-sm bg-white font-body overflow-hidden" data-id="element-5500" data-mp="1evtyljs1y428">
      <div className="px-6 py-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-start justify-between gap-2" data-id="element-5501" data-mp="10wb43ts5yr8e">
        <div data-id="element-5502" data-mp="37jrz02768nd5">
          <div className="flex items-center gap-2 flex-wrap" data-id="element-5503" data-mp="37ji9sgtyak8i">
            <h2 className="text-lg font-bold text-[#0F172A] font-heading" data-id="element-5504" data-mp="37luz5f21fwvl">
              {t('Votre briefing territorial', 'Your territorial briefing')}
            </h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-[#4F46E5] text-[11px] font-semibold" data-id="element-5505" data-mp="2xuryged0tjwu">
              <Sparkles className="w-3 h-3" data-id="element-5506" data-mp="2tqk5u63fzjtc" />
              {t('Préparé par ATNA', 'Prepared by ATNA')}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-5507" data-mp="3ljb6css5ruoe">
            {scopeLabel ? t(`Les enjeux du ${scopeLabel.fr}, avec les demandes liées regroupées par situation.`, `The issues in ${scopeLabel.en}, with related requests grouped by situation.`) : t('Les enjeux de la ville, avec les demandes liées regroupées par situation.', 'The city’s issues, with related requests grouped by situation.')}
          </p>
        </div>
        <span className="text-xs text-slate-400 shrink-0" data-id="element-5508" data-mp="gjuadyq79pk0">{DATA_AS_OF[lang]}</span>
      </div>

      <div className="px-6 pt-5" data-id="element-5509" data-mp="linn3oxnxcj4">
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3" data-id="element-5510" data-mp="pltskucyazoj">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-2" data-id="element-5511" data-mp="uctr241pxpt5">
            <History className="w-3.5 h-3.5" data-id="element-5512" data-mp="zcf1bamc9kl6" />
            {t('Depuis votre dernière visite', 'Since your last visit')}
            <span className="font-normal text-slate-400" data-id="element-5513" data-mp="14agkrwnoflx6">· {since.lastVisit[lang]}</span>
          </div>
          <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-5514" data-mp="19a2xm4zlf05g">
            {sinceCols.map(c => {
            const Icon = c.icon;
            const zero = c.data.count === 0;
            return <div key={c.key} className="flex items-start gap-2.5" data-id="element-5515" data-mp="3b2r3km3mg1ox">
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${zero ? 'text-slate-300' : c.color}`} data-id="element-5516" data-mp="3f2rh84iwc8ux" />
                  <div className="min-w-0" data-id="element-5517" data-mp="2jsl82oz5nn4n">
                    <dt className="text-xs text-slate-500" data-id="element-5518" data-mp="2jrjnntw91oa9">{c.label}</dt>
                    <dd className="text-sm" data-id="element-5519" data-mp="2fqhjqzre0o1y">
                      <span className={`font-bold font-heading ${zero ? 'text-slate-400' : 'text-[#0F172A]'}`} data-id="element-5520" data-mp="2ftlygr2hb26v">
                        {c.data.count}
                      </span>{' '}
                      <span className="text-slate-600" data-id="element-5521" data-mp="2ot0ib7o54eie">{c.data.detail[lang]}</span>
                    </dd>
                  </div>
                </div>;
          })}
          </dl>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-4" data-id="element-5522" data-mp="1w1pafb80pxph">
        {briefing.issues.map(item => <BriefingCard key={item.id} item={item} sentKinds={sent} onPrepare={d => setDraft({
        itemId: item.id,
        draft: d
      })} onOpen={onOpen} data-id="element-5523" data-mp="261py19cx6zzh" />)}
        <div className="lg:col-span-3" data-id="element-5524" data-mp="1ddu246v6dc3r">
          <BriefingCard item={briefing.result} sentKinds={sent} onPrepare={d => setDraft({
          itemId: briefing.result.id,
          draft: d
        })} onOpen={onOpen} wide data-id="element-5525" data-mp="1dcsel2wz2x55" />
        </div>
      </div>

      <BriefingDraftDialog draft={draft?.draft ?? null} onClose={closeDraft} onSend={send} data-id="element-5526" data-mp="3g094sieonwg1" />
    </section>;
}